import { useState } from 'react';

import { View } from 'react-native';
import { Text } from '@/components/Text';

import { PrimaryButton } from '@/components/Button';
import { useForm } from 'react-hook-form';
import { serverForgotPassword } from '@/stores/auth/services';

import LoadingIndicator from '@/components/SharedComponents/LoadingIndicator/LoadingIndicator';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { useDialog } from '@/hooks/useDialog';
import { isAxiosError } from 'axios';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';
import { isPatientApp } from '@/config/constants';
import { Button } from '@/components/ui/therapist/Button';
import { SharedFormField } from '@/components/SharedComponents/FormField/SharedFormField';

interface ForgotPasswordFormData {
  email: string;
}

type RootStackParamList = {
  ResetPassword: { email: string };
};
type ResetPassword = RouteProp<RootStackParamList, 'ResetPassword'>;

const instructionsTitle = 'Überprüfen Sie Ihren E-Mail-Posteingang';
const instructionsText =
  'Eine Anleitung zum Zurücksetzen des Passworts wurde an Ihre E-Mail-Adresse versandt. Bitte überprüfen Sie Ihr E-Mail-Postfach.';

const errTextOne =
  'Fehler beim Zurücksetzen des Passworts. Überprüfen Sie Ihre E-Mail-Adresse und versuchen Sie es noch einmal';
const errTextTwo =
  'Fehler beim Bearbeiten Ihrer Anfrage. Überprüfen Sie Ihre E-Mail-Adresse und versuchen es erneut.';

export function ForgotPasswordForm() {
  const { showDialog, removeDialog } = useDialog();
  const { goBack } = useNavigation();
  const { params } = useRoute<ResetPassword>();
  const { control, handleSubmit } = useForm<ForgotPasswordFormData>({
    mode: 'onBlur',
    defaultValues: {
      email: params.email ?? '',
    },
  });
  const [errText, setErrText] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const intstructionsSentDialog = () => {
    showDialog({
      title: instructionsTitle,
      content: instructionsText,
      cancelable: false,
      confirmAction: {
        label: 'OK',
        onPress: () => {
          goBack();
          removeDialog();
        },
      },
    });
  };

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setErrText(undefined);
    setIsLoading(true);

    try {
      data.email = data.email.trim();
      const response = await serverForgotPassword(data);
      setIsLoading(false);
      if (response.status !== 200) {
        setErrText(errTextOne);
      } else {
        intstructionsSentDialog();
      }
    } catch (e) {
      setIsLoading(false);
      const axiosError = isAxiosError(e);
      if (!axiosError) {
        return setErrText(errTextTwo);
      }
      if (e.code !== 'ERR_NETWORK') {
        return setErrText(errTextTwo);
      }
      showDialog(PREPARED_DIALOGS.noInternetDialog());
    }
  };

  return (
    <>
      <LoadingIndicator showLoading={isLoading} />
      <View className="pb-6">
        <Text className="text-xl">
          Geben Sie Ihre E-Mail-Adresse ein, um Ihr Passwort zurückzusetzen.
        </Text>
      </View>
      <View>
        <SharedFormField
          control={control}
          name="email"
          label="E-Mail"
          useEmailInput
          rules={{
            required: 'Pflichtfeld',
            pattern: {
              value:
                /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/gi,
              message: 'E-Mail-Adresse ungültig',
            },
          }}
        />
      </View>
      {!!errText && (
        <View className="p-2">
          <Text className="font-semibold text-red-600 text-lg">{errText}</Text>
        </View>
      )}
      <View className="flex-row gap-4 mt-1">
        <View className="flex-1">
          {isPatientApp ? (
            <PrimaryButton look="neutral" onPress={goBack} label="Abbrechen" />
          ) : (
            <Button
              style={{ justifyContent: 'center', alignItems: 'center' }}
              look="secondary"
              onPress={goBack}
              label="Abbrechen"
            />
          )}
        </View>
        <View className="flex-1">
          {isPatientApp ? (
            <PrimaryButton
              onPress={handleSubmit(onSubmit)}
              label="Passwort zurücksetzen"
            />
          ) : (
            <Button
              style={{ justifyContent: 'center', alignItems: 'center' }}
              onPress={handleSubmit(onSubmit)}
              label="Passwort zurücksetzen"
            />
          )}
        </View>
      </View>
    </>
  );
}
